import { useState } from 'react'
import type { Concert } from '../domain/program-schema.ts'
import { formatDayLabel, formatTimeRange } from '../domain/time.ts'
import { t } from '../i18n.ts'
import { useAuth } from '../store/AuthProvider.tsx'
import { NotificationSetup } from './NotificationSetup.tsx'
import './ShareSheet.css'

type Channel = 'push' | 'email' | 'link'
type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; channel: Channel }
  | { kind: 'failed' }

const CHANNEL_MESSAGE: Record<Channel, string> = {
  push: t.share.sentPush,
  email: t.share.sentEmail,
  link: t.share.sentLink,
}

/**
 * Sends a concert and the sender's own note to a friend.
 *
 * The copy-link action is always available and needs no backend at all, so the
 * feature never dead-ends: whatever happens with push, email, sign-in or the
 * network, the sender can still get the concert to someone.
 */
export function ShareSheet({ concert }: { concert: Concert }) {
  const { cloudEnabled, user, getIdToken } = useAuth()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })
  const [copied, setCopied] = useState(false)

  const url = `${window.location.origin}/konsert/${concert.id}`

  async function copyLink() {
    const shareData = { title: concert.name, text: `${concert.name} — Øya annotated`, url }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
        return
      }
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      // The user dismissed the OS share sheet, or the clipboard was refused.
    }
  }

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setState({ kind: 'sending' })
    try {
      const token = await getIdToken()
      const response = await fetch('/api/share', {
        method: 'POST',
        // The server can legitimately spend a while on push and SMTP. Without a
        // deadline the button sits on "Sender …" forever on a bad connection, with
        // no error and no way to retry.
        signal: AbortSignal.timeout(15_000),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
        body: JSON.stringify({
          concertId: concert.id,
          email,
          concertName: concert.name,
          concertTime: `${formatDayLabel(concert.day)} ${formatTimeRange(concert)}`,
          stage: concert.stage,
        }),
      })
      if (!response.ok) {
        setState({ kind: 'failed' })
        return
      }
      const body = (await response.json()) as { channel: Channel }
      setState({ kind: 'sent', channel: body.channel })
      setEmail('')
    } catch {
      setState({ kind: 'failed' })
    }
  }

  return (
    <section className="share" aria-label={t.concert.share}>
      <h2 className="share__title">{t.concert.share}</h2>

      {cloudEnabled && user ? (
        <>
          <form className="share__form" onSubmit={send}>
            <label className="sr-only" htmlFor="share-email">
              {t.share.emailLabel}
            </label>
            <input
              id="share-email"
              className="share__input"
              type="email"
              required
              inputMode="email"
              autoComplete="email"
              placeholder={t.share.emailPlaceholder}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (state.kind !== 'idle') setState({ kind: 'idle' })
              }}
            />
            <button type="submit" className="share__send" disabled={state.kind === 'sending'}>
              {state.kind === 'sending' ? t.share.sending : t.share.send}
            </button>
          </form>

          {/* R27. Sending on e-post also proposes a friendship, and a proposal the
              other person accepts opens every note either of them writes — so the
              sender is told before the send, not after. */}
          <p className="share__discloses">{t.share.alsoProposes}</p>

          {state.kind === 'sent' && (
            <p className="share__status share__status--ok" role="status">
              {CHANNEL_MESSAGE[state.channel]}
            </p>
          )}
          {state.kind === 'failed' && (
            <p className="share__status share__status--error" role="status">
              {t.share.failed}
            </p>
          )}

          <NotificationSetup />
        </>
      ) : (
        <p className="share__hint">{cloudEnabled ? t.share.signInToShare : t.share.linkOnly}</p>
      )}

      <button type="button" className="share__copy" onClick={() => void copyLink()}>
        {copied ? t.share.copied : t.share.copyLink}
      </button>
    </section>
  )
}
